import { create } from 'zustand'

/**
 * Audience Targeting Store with manual localStorage control
 *
 * Data structure: { [stage]: { [audience]: { countries: { included: [], excluded: [] }, ... } } }
 *
 * Features:
 * - Stage + Audience keyed settings
 * - Manual localStorage control via loadFromLocalStorage()
 * - Extensible for future targeting options (age, gender, interests, etc.)
 */
const useAudienceTargetingStore = create((set, get) => ({
    // Store structure: { [stage]: { [audience]: { countries: { included: [], excluded: [] }, ... } } }
    settings: {},

    // Get settings for a specific stage and audience
    getAudienceSettings: (stage, audience) => {
        const { settings } = get()
        return settings[stage]?.[audience] || {}
    },

    // Get country settings for a specific stage and audience
    getCountrySettings: (stage, audience) => {
        const { settings } = get()
        if (!stage && !audience) return settings

        return settings[stage]?.[audience]?.countries || { included: [], excluded: [] }
    },

    // Get custom event type for a specific stage and audience
    getCustomEventType: (stage, audience) => {
        const { settings } = get()
        return settings[stage]?.[audience]?.custom_event_type || null
    },

    // Set custom event type for a specific stage and audience
    setCustomEventType: (stage, audience, customEventType) => {
        set((state) => {
            const currentSettings = state.settings[stage]?.[audience] || {}

            return {
                settings: {
                    ...state.settings,
                    [stage]: {
                        ...state.settings[stage],
                        [audience]: {
                            ...currentSettings,
                            custom_event_type: customEventType,
                        },
                    },
                },
            }
        })
    },

    // Get recency days for a specific stage and audience
    getRecencyDays: (stage, audience) => {
        const { settings } = get()
        return settings[stage]?.[audience]?.recency_days || null
    },

    // Set recency days for a specific stage and audience
    setRecencyDays: (stage, audience, recencyDays) => {
        set((state) => {
            const currentSettings = state.settings[stage]?.[audience] || {}

            return {
                settings: {
                    ...state.settings,
                    [stage]: {
                        ...state.settings[stage],
                        [audience]: {
                            ...currentSettings,
                            recency_days: recencyDays,
                        },
                    },
                },
            }
        })
    },

    // Add a country to included list
    addIncludedCountry: (stage, audience, countryCode) => {
        set((state) => {
            const currentSettings = state.settings[stage]?.[audience] || {}
            const currentCountries = currentSettings.countries || { included: [], excluded: [] }

            return {
                settings: {
                    ...state.settings,
                    [stage]: {
                        ...state.settings[stage],
                        [audience]: {
                            ...currentSettings,
                            countries: {
                                included: [
                                    ...currentCountries.included.filter((code) => code !== countryCode),
                                    countryCode,
                                ],
                                excluded: currentCountries.excluded.filter((code) => code !== countryCode),
                            },
                        },
                    },
                },
            }
        })
    },

    // Add a country to excluded list
    addExcludedCountry: (stage, audience, countryCode) => {
        set((state) => {
            const currentSettings = state.settings[stage]?.[audience] || {}
            const currentCountries = currentSettings.countries || { included: [], excluded: [] }

            return {
                settings: {
                    ...state.settings,
                    [stage]: {
                        ...state.settings[stage],
                        [audience]: {
                            ...currentSettings,
                            countries: {
                                included: currentCountries.included.filter((code) => code !== countryCode),
                                excluded: [
                                    ...currentCountries.excluded.filter((code) => code !== countryCode),
                                    countryCode,
                                ],
                            },
                        },
                    },
                },
            }
        })
    },

    // Remove a country from both included and excluded lists
    removeCountry: (stage, audience, countryCode) => {
        set((state) => {
            const currentSettings = state.settings[stage]?.[audience] || {}
            const currentCountries = currentSettings.countries || { included: [], excluded: [] }

            return {
                settings: {
                    ...state.settings,
                    [stage]: {
                        ...state.settings[stage],
                        [audience]: {
                            ...currentSettings,
                            countries: {
                                included: currentCountries.included.filter((code) => code !== countryCode),
                                excluded: currentCountries.excluded.filter((code) => code !== countryCode),
                            },
                        },
                    },
                },
            }
        })
    },

    // Update any setting for a specific stage and audience (extensible for future settings)
    updateAudienceSetting: (stage, audience, settingKey, settingValue) => {
        set((state) => ({
            settings: {
                ...state.settings,
                [stage]: {
                    ...state.settings[stage],
                    [audience]: {
                        ...state.settings[stage]?.[audience],
                        [settingKey]: settingValue,
                    },
                },
            },
        }))
    },

    // Clear all settings for a specific stage and audience
    clearAudienceSettings: (stage, audience) => {
        set((state) => {
            const stageSettings = state.settings[stage]
            if (!stageSettings) return state

            const { [audience]: _, ...remainingAudiences } = stageSettings

            return {
                settings: {
                    ...state.settings,
                    [stage]: remainingAudiences,
                },
            }
        })
    },

    // Clear all settings for a stage
    clearStageSettings: (stage) => {
        set((state) => {
            const { [stage]: _, ...remainingSettings } = state.settings
            return { settings: remainingSettings }
        })
    },

    // Clear all settings
    clearAllSettings: () => {
        set({ settings: {} })
    },

    // Load settings from external source (like wizard data or localStorage)
    loadSettings: (externalSettings) => {
        set({ settings: externalSettings || {} })
    },
}))

export default useAudienceTargetingStore
